import { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { glassCardSx } from "src/theme/glassDialog";
import FrostedCard from "./FrostedCard";
import SpecGuideResult from "./SpecGuideResult";
import { rasitusluokkaTree, canonicaliseRasitusluokka } from "src/utils/quote/rasitusluokat";
import { kayttokohdePresets } from "src/utils/quote/kayttokohdePresets";
import { buildBetoniLaatuSelvitys } from "src/utils/quote/betoniLaatuSelvitys";
import { lujuusluokkaOptions } from "src/utils/quote/quoteConstants";
import { useT } from "src/i18n";

const EMPTY_PRESET = { answers: {}, notkeusId: "", raeKokoId: "" };

// rasitusluokat.js terminals carry `lujuusluokkaMin` as the human-readable
// short code ("C30/37"). Translate to the betoniLujuus.lujuusId FK that the
// backend expects. Returns "" when no match — the avustin should never produce
// an unmatched class, but the empty sentinel keeps Select happy.
function lujuusIdFromShortCut(shortCut, t) {
    const hit = lujuusluokkaOptions(t).find((o) => o.label === shortCut);
    return hit ? hit.value : "";
}

// Resolve a single answer to the canonical option id. Accepts either an id
// (current format) or a label (pre-2026-05-12 anonymous-draft format) and
// returns the id. Idempotent on already-id values; returns the input unchanged
// if neither matches so the caller can fail gracefully.
function optionIdFor(options, raw) {
    if (raw == null) return raw;
    if (options.find((o) => o.id === raw)) return raw;
    const byLabel = options.find((o) => o.label === raw);
    return byLabel?.id ?? raw;
}

// One-shot migration applied at mount: rewrites any legacy label-keyed
// guidedAnswers to id-keyed. Subsequent picks already write ids, so this
// helper can be deleted once pre-refactor anonymous drafts have all expired.
function migrateLegacyAnswers(tree, rawAnswers) {
    const result = {};
    for (const [nodeId, raw] of Object.entries(rawAnswers ?? {})) {
        const node = tree.nodes[nodeId];
        result[nodeId] = node?.options ? optionIdFor(node.options, raw) : raw;
    }
    return result;
}

function unlockChain(tree, answers) {
    const chain = [];
    let nodeId = tree.start;
    let terminal = null;
    while (nodeId) {
        const node = tree.nodes[nodeId];
        if (!node) break;
        if (node.terminal) {
            terminal = node;
            break;
        }
        chain.push({ nodeId, node });
        const answerId = answers[nodeId];
        if (answerId == null) break;
        const opt = node.options.find((o) => o.id === answerId);
        if (!opt) break;
        nodeId = opt.next;
    }
    return { chain, terminal };
}

function QuestionCard({ question, help, value, options, onPick }) {
    return (
        <FrostedCard sx={{ p: 2 }}>
            <FormControl>
                <FormLabel sx={{ fontWeight: 500, mb: help ? 0.5 : 1, color: "text.primary" }}>
                    {question}
                </FormLabel>
                {help && (
                    <Box sx={{ fontSize: 12, color: "text.secondary", mb: 1, lineHeight: 1.5 }}>
                        {help}
                    </Box>
                )}
                <RadioGroup value={value || ""} onChange={(e) => onPick(e.target.value)}>
                    {options.map((opt) => (
                        <FormControlLabel
                            key={opt.id}
                            value={opt.id}
                            control={<Radio size="small" sx={{ alignSelf: "flex-start", mt: opt.description ? "2px" : 0 }} />}
                            sx={{ alignItems: "flex-start", mb: opt.description ? 0.75 : 0 }}
                            label={
                                opt.description ? (
                                    <Box sx={{ py: 0.25 }}>
                                        <Box sx={{ fontSize: 14, color: "text.primary" }}>{opt.label}</Box>
                                        <Box sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.5, mt: 0.25 }}>
                                            {opt.description}
                                        </Box>
                                    </Box>
                                ) : (
                                    opt.label
                                )
                            }
                        />
                    ))}
                </RadioGroup>
            </FormControl>
        </FrostedCard>
    );
}

export default function SpecGuideModal({
    open,
    onClose,
    kayttokohde,
    initialAnswers,
    initialKayttoIkaId,
    onApply,
}) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const t = useT();

    const tree = useMemo(() => rasitusluokkaTree(t), [t]);
    const presets = useMemo(() => kayttokohdePresets(t), [t]);
    const preset = presets[kayttokohde] ?? EMPTY_PRESET;
    const postQuestion = preset.postQuestion ?? null;

    // Initial state computed once per mount. Parent passes `key={mountKey}` to
    // force a fresh mount on each open, which naturally resets state without an
    // effect-and-setState dance.
    const [answers, setAnswers] = useState(() =>
        migrateLegacyAnswers(tree, {
            ...preset.answers,
            ...(initialAnswers ?? {}),
        }),
    );
    const [postAnswer, setPostAnswer] = useState(() =>
        postQuestion && initialAnswers
            ? optionIdFor(postQuestion.options, initialAnswers[postQuestion.id] ?? null)
            : null,
    );
    const [kayttoIkaId, setKayttoIkaId] = useState(() => initialKayttoIkaId || 1);

    const { chain, terminal } = useMemo(() => unlockChain(tree, answers), [tree, answers]);

    // Auto-scroll: bring newly-revealed content into view after each answer.
    // Watching chain.length + postAnswer + terminal id covers question reveals,
    // post-question reveal, and result panel reveal. scrollIntoView is a DOM call
    // (not setState), so it's permitted inside useEffect.
    const bottomRef = useRef(null);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [chain.length, postAnswer, terminal?.result]);

    function handlePick(nodeId, optionId) {
        const truncated = {};
        for (const { nodeId: id } of chain) {
            if (id === nodeId) break;
            truncated[id] = answers[id];
        }
        truncated[nodeId] = optionId;
        setAnswers(truncated);
        setPostAnswer(null);
    }

    // "Aloita alusta" clears every answer including the käyttökohde preset, so the
    // user falls back to answering the full question chain. Resetting only to the
    // preset would be a no-op on fully-resolved käyttökohdes (anturat, sokkeli, …),
    // which is what looks broken to the user.
    function handleStartOver() {
        setAnswers({});
        setPostAnswer(null);
    }

    const hasAnyAnswers = Object.keys(answers).length > 0 || postAnswer != null;

    const effective = useMemo(() => {
        if (!terminal) return null;
        let resolvedTerminal = terminal;
        let notkeusId = preset.notkeusId ?? "";
        let raeKokoId = preset.raeKokoId ?? "";
        if (postQuestion && postAnswer) {
            const opt = postQuestion.options.find((o) => o.id === postAnswer);
            if (opt?.override) {
                resolvedTerminal = tree.nodes[opt.override.terminalId] ?? terminal;
                if (opt.override.notkeusId != null) notkeusId = opt.override.notkeusId;
                if (opt.override.raeKokoId != null) raeKokoId = opt.override.raeKokoId;
            }
        }
        return { terminal: resolvedTerminal, notkeusId, raeKokoId };
    }, [tree, terminal, postQuestion, postAnswer, preset.notkeusId, preset.raeKokoId]);

    // Map preset answer ids back to their display labels for the caption.
    // Falls back to the raw id if the option isn't found (defensive).
    const presetActiveValues = Object.keys(preset.answers)
        .filter((k) => answers[k] === preset.answers[k])
        .map((k) => {
            const node = tree.nodes[k];
            const opt = node?.options?.find((o) => o.id === preset.answers[k]);
            return opt?.label ?? preset.answers[k];
        });
    const showPresetCaption = false;// Removed, unnecessary bloat on page. presetActiveValues.length > 0;

    const showPostQuestion = !!terminal && !!postQuestion;
    const isResolved = !!effective && (!postQuestion || postAnswer != null);

    function handleApply() {
        if (!isResolved) return;
        const guidedAnswers = {
            ...answers,
            ...(postQuestion ? { [postQuestion.id]: postAnswer } : {}),
        };
        onApply({
            specMode: "guided",
            rasitusluokka: canonicaliseRasitusluokka(effective.terminal.result),
            lujuusId: lujuusIdFromShortCut(effective.terminal.lujuusluokkaMin, t),
            notkeusId: effective.notkeusId,
            raeKokoId: effective.raeKokoId,
            kayttoIkaId,
            guidedAnswers,
            betoniLaatuSelvitys: buildBetoniLaatuSelvitys(kayttokohde, guidedAnswers, t),
        });
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        // Was hand-rolled here, and the border never rendered: cardBorder is
                        // already a full shorthand ("1px solid rgba(…)"), so wrapping it in
                        // another `1px solid ${…}` emitted invalid CSS the parser dropped.
                        ...glassCardSx(theme),
                        backgroundImage: "none",
                    },
                },
                backdrop: { sx: { backdropFilter: "blur(8px) saturate(120%)" } },
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: "'Black Ops One', serif",
                    letterSpacing: "0.06em",
                    fontSize: 18,
                    pr: 7,
                }}
            >
                {t("wizard.specGuideModal.title")}
                <IconButton
                    onClick={onClose}
                    sx={{ position: "absolute", right: 12, top: 12, color: "text.secondary" }}
                    size="small"
                    aria-label={t("common.dialog.close")}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: "divider" }}>
                <Stack spacing={1.5}>
                    <Box sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55 }}>
                        {t("wizard.specGuideModal.intro")}
                    </Box>

                    {showPresetCaption && (
                        <Box sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>
                            {t("wizard.specGuideModal.presetCaption")}{" "}
                            <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
                                {presetActiveValues.join(" · ")}
                            </Box>
                        </Box>
                    )}

                    {chain.length === 0 && !terminal && (
                        <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                            {t("wizard.specGuideModal.selectKayttokohdeFirst")}
                        </Box>
                    )}

                    {chain.map(({ nodeId, node }) => (
                        <QuestionCard
                            key={nodeId}
                            question={node.question}
                            help={node.help}
                            value={answers[nodeId]}
                            options={node.options}
                            onPick={(label) => handlePick(nodeId, label)}
                        />
                    ))}

                    {showPostQuestion && (
                        <QuestionCard
                            question={postQuestion.question}
                            help={postQuestion.help}
                            value={postAnswer}
                            options={postQuestion.options}
                            onPick={setPostAnswer}
                        />
                    )}

                    {isResolved && (
                        <SpecGuideResult
                            terminal={effective.terminal}
                            kayttoIkaId={kayttoIkaId}
                            onKayttoIkaChange={setKayttoIkaId}
                        />
                    )}
                    <Box ref={bottomRef} aria-hidden="true" />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>
                    {t("wizard.shared.cancel")}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    onClick={handleStartOver}
                    disabled={!hasAnyAnswers}
                    sx={{ color: "text.secondary", textTransform: "none" }}
                >
                    {t("wizard.shared.startOver")}
                </Button>
                <Button
                    onClick={handleApply}
                    disabled={!isResolved}
                    sx={{
                        textTransform: "none",
                        background: w.amberGradient ?? "#E8893B",
                        color: "#1A1A1A",
                        px: 3,
                        fontWeight: 600,
                        boxShadow: w.amberShadow ?? "0 8px 24px rgba(232,137,59,0.35)",
                        "&:hover": {
                            background: w.amberGradient ?? "#E8893B",
                            boxShadow: w.amberShadowHover ?? "0 14px 32px rgba(232,137,59,0.45)",
                        },
                        "&.Mui-disabled": {
                            background: "transparent",
                            color: "text.disabled",
                            boxShadow: "none",
                        },
                    }}
                >
                    {t("wizard.specGuideModal.applyThese")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
